
import { useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { User } from '@supabase/supabase-js';
import type { Profile } from '../types';
import { toast } from 'sonner';
import { sendWelcomeEmailDirect } from '@/utils/notifications/notificationSender';

/**
 * Hook for profile fetching operations, with debug for 406 errors
 */
export const useProfileFetching = (
  user: User | null,
  setProfile: (profile: Profile | null) => void
) => {
  const [isLoading, setIsLoading] = useState(false);
  // Track if we've already attempted to create a profile for the current user
  const profileCreationAttempted = useRef<Set<string>>(new Set());

  const fetchProfile = useCallback(async (userId: string) => {
    console.log('[ProfileFetch] Attempting to fetch profile for user:', userId);
    setIsLoading(true);

    try {
      // Add debug: what headers are sent?
      const debugHeaders = supabase
        .from('profiles')
        .select('*')
        .eq('id', userId);

      console.log('[ProfileFetch] Supabase profile request builder:', debugHeaders);

      // Use maybeSingle() to allow "not found" state without an exception
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      // Handle error case (e.g. DB connection issues, etc.)
      if (error) {
        console.error('[ProfileFetch] Error fetching profile:', error);

        // Specific handling for no data found
        if (error.code === 'PGRST116' && !profileCreationAttempted.current.has(userId)) {
          console.log('[ProfileFetch] No profile found (error PGRST116). Attempting fallback profile creation...');
          await createProfileAndSendWelcome(userId);
        } else {
          toast.error('Failed to load profile');
          setProfile(null);
        }
        setIsLoading(false);
        return null;
      }

      // Handle the case when there's no error but profile is null (Google sign-in)
      if (!profile && !profileCreationAttempted.current.has(userId)) {
        console.log('[ProfileFetch] No profile found (no error, but data is null). Creating profile for Google user...');
        await createProfileAndSendWelcome(userId);
        setIsLoading(false);
        return null;
      }

      // If we got here and there's no profile data but we've already tried to create one,
      // something is wrong - bail out
      if (!profile) {
        console.warn('[ProfileFetch] Fetched but got no profile data. Likely new account, no row yet.');
        setProfile(null);
        setIsLoading(false);
        return null;
      }

      console.log('[ProfileFetch] Profile fetched successfully:', profile?.id, profile);
      setProfile(profile as Profile);
      
      // ENHANCED: Check Google avatar on EVERY login, not just when profile.avatar_url is null
      if (user?.user_metadata?.avatar_url) {
        try {
          // Only update if the avatar_url is different or missing
          if (!profile.avatar_url || profile.avatar_url !== user.user_metadata.avatar_url) {
            console.log('[ProfileFetch] Updating profile with Google avatar:', user.user_metadata.avatar_url);
            
            const { error: updateError } = await supabase
              .from('profiles')
              .update({ avatar_url: user.user_metadata.avatar_url })
              .eq('id', userId);
              
            if (!updateError) {
              // Update local profile state with the Google avatar
              setProfile({
                ...profile as Profile,
                avatar_url: user.user_metadata.avatar_url
              });
              console.log('[ProfileFetch] Successfully updated avatar from Google metadata');
            } else {
              console.error('[ProfileFetch] Error updating avatar from Google:', updateError);
            }
          } else {
            console.log('[ProfileFetch] Avatar already up to date with Google metadata');
          }
        } catch (updateErr) {
          console.error('[ProfileFetch] Exception updating avatar from Google:', updateErr);
        }
      }
      
      setIsLoading(false);
      return profile as Profile;
    } catch (error) {
      console.error('[ProfileFetch] Exception fetching profile:', error);

      // More user-friendly error message
      toast.error('An unexpected error occurred loading your profile');
      setProfile(null);
      setIsLoading(false);
      return null;
    }
  }, [setProfile, user]);

  // Helper function for creating profile and sending welcome email
  // Extracted to avoid code duplication between error and null-data paths
  const createProfileAndSendWelcome = async (userId: string) => {
    profileCreationAttempted.current.add(userId);
    
    try {
      const userData = await supabase.auth.getUser();
      const userMetadata = userData.data?.user?.user_metadata;
      
      // Check if user has Google avatar in metadata
      const googleAvatarUrl = userMetadata?.avatar_url;
      const userName = userMetadata?.name || 'User';
      const userEmail = userData.data?.user?.email;
      
      if (!userEmail) {
        console.error('[ProfileFetch] Cannot create profile: user email not found');
        setProfile(null);
        return;
      }
      
      console.log('[ProfileFetch] Creating profile for user:', userName, userEmail);

      const { data: newProfile, error: insertError } = await supabase
        .from('profiles')
        .insert([
          {
            id: userId,
            email: userEmail,
            name: userName,
            role: 'public',
            first_name: (userName).split(' ')[0],
            last_name: (userName).split(' ').length > 1 ?
              (userName).split(' ').slice(1).join(' ') : null,
            avatar_url: googleAvatarUrl || null,
            id_verification_status: null // Explicitly set to null for new profiles
          }
        ])
        .select('*')
        .maybeSingle();
      
      if (insertError) {
        console.error('[ProfileFetch] Error creating profile directly:', insertError);
        setProfile(null);
      } else {
        console.log('[ProfileFetch] Profile created successfully:', newProfile);
        setProfile(newProfile as Profile);
        
        // Send welcome email - USING DIRECT METHOD to avoid circular dependency
        try {
          console.log('[ProfileFetch] Sending welcome email to new user:', userEmail);
          
          // Send welcome email with direct sender function
          const emailResult = await sendWelcomeEmailDirect(userEmail, userName);
          console.log('[ProfileFetch] Welcome email result:', emailResult);
        } catch (emailErr) {
          console.error('[ProfileFetch] Error sending welcome email:', emailErr);
          // Non-blocking - we don't want profile creation to fail if email fails
        }
      }
    } catch (createErr) {
      console.error('[ProfileFetch] Exception creating profile:', createErr);
      setProfile(null);
    }
  };

  return {
    fetchProfile,
    isLoading
  };
};
